import React, { useEffect, useMemo, useState } from 'react';
import {
  Users,
  CalendarDays,
  Clock3,
  CheckCircle2,
  RefreshCw,
  ShieldCheck,
  Mail,
  UserRound,
  FileText,
  Download,
  Printer,
} from 'lucide-react';

import {
  getContractorOperationsOverview,
} from '../api/contractorOnboardingPortalApi';

const StatCard = ({
  icon: Icon,
  label,
  value,
}) => (
  <div className="rounded-xl border border-zinc-800 bg-[#151517] p-5">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm text-zinc-400">
          {label}
        </p>

        <p className="mt-2 text-3xl font-semibold text-white">
          {value ?? 0}
        </p>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-900/70 p-2.5">
        <Icon className="h-5 w-5 text-zinc-400" />
      </div>
    </div>
  </div>
);


const formatCurrency = (value) => {
  const number = Number(value || 0);

  return number.toLocaleString(
    'en-AU',
    {
      style: 'currency',
      currency: 'AUD',
    },
  );
};


const formatComplianceDate = (value) => {
  if (!value) {
    return 'Not recorded';
  }

  const parsed = new Date(`${value}T00:00:00`);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString(
    'en-AU',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
  );
};


const ComplianceBadge = ({
  item,
}) => {
  const status = item?.status || 'missing';

  const classes = {
    current:
      'bg-emerald-500/10 text-emerald-400',
    warning:
      'bg-amber-500/10 text-amber-300',
    critical:
      'bg-orange-500/10 text-orange-300',
    expired:
      'bg-red-500/10 text-red-400',
    missing:
      'bg-zinc-800 text-zinc-400',
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        classes[status] || classes.missing
      }`}
    >
      {item?.status_label || 'Missing'}
    </span>
  );
};



const getShiftLifecycle = (shift = {}) => {
  const value = [
    shift.workflow_stage,
    shift.shift_status,
    shift.acceptance_status,
    shift.response_status,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replaceAll('-', '_')
    .replaceAll(' ', '_');

  if (value.includes('cancel')) {
    return {
      key: 'cancelled',
      group: 'exception',
      label: 'Cancelled',
      detail: 'This allocation was cancelled.',
      className: 'bg-zinc-800 text-zinc-300',
    };
  }

  if (value.includes('declin')) {
    return {
      key: 'declined',
      group: 'exception',
      label: 'Declined',
      detail: 'A replacement or follow-up may be required.',
      className: 'bg-red-500/10 text-red-300',
    };
  }

  if (value.includes('auto_closed') || value.includes('closed')) {
    return {
      key: 'closed',
      group: 'exception',
      label: 'Closed',
      detail: 'Closed without a completed workflow.',
      className: 'bg-orange-500/10 text-orange-300',
    };
  }

  if (value.includes('completed') || value.includes('complete')) {
    return {
      key: 'completed',
      group: 'completed',
      label: 'Completed',
      detail: 'Awaiting timesheet review where applicable.',
      className: 'bg-emerald-500/10 text-emerald-300',
    };
  }

  if (
    value.includes('in_progress')
    || value.includes('clocked_in')
    || value.includes('checked_in')
    || value.includes('signed_on')
  ) {
    return {
      key: 'in_progress',
      group: 'in_progress',
      label: 'In Progress',
      detail: 'Officer has commenced this shift.',
      className: 'bg-violet-500/10 text-violet-300',
    };
  }

  if (value.includes('accepted')) {
    return {
      key: 'accepted',
      group: 'accepted',
      label: 'Accepted',
      detail: 'Officer has confirmed attendance.',
      className: 'bg-sky-500/10 text-sky-300',
    };
  }

  if (
    value.includes('pending')
    || value.includes('unaccepted')
    || value.includes('scheduled')
  ) {
    return {
      key: 'pending',
      group: 'pending',
      label: 'Pending Acceptance',
      detail: 'Waiting for the officer to respond.',
      className: 'bg-amber-500/10 text-amber-300',
    };
  }

  return {
    key: 'allocated',
    group: 'pending',
    label: 'Allocated',
    detail: 'Waiting for lifecycle activity.',
    className: 'bg-blue-500/10 text-blue-300',
  };
};


const normaliseList = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (!value || typeof value !== 'object') {
    return [];
  }

  for (const key of ['items', 'rows', 'results', 'data', 'workers', 'members']) {
    if (Array.isArray(value[key])) {
      return value[key];
    }
  }

  return [];
};



const toLocalIsoDate = (value) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};


const addDays = (value, amount) => {
  const result = new Date(
    value.getFullYear(),
    value.getMonth(),
    value.getDate(),
  );

  result.setDate(result.getDate() + amount);

  return result;
};


const getPeriodPreset = (preset) => {
  const today = new Date();
  const localToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  if (preset === 'last_week') {
    const thisWeekMonday = addDays(
      localToday,
      -((localToday.getDay() + 6) % 7),
    );
    const start = addDays(thisWeekMonday, -7);

    return {
      startDate: toLocalIsoDate(start),
      endDate: toLocalIsoDate(addDays(start, 6)),
    };
  }

  if (preset === 'this_month') {
    const start = new Date(
      localToday.getFullYear(),
      localToday.getMonth(),
      1,
    );
    const end = new Date(
      localToday.getFullYear(),
      localToday.getMonth() + 1,
      0,
    );

    return {
      startDate: toLocalIsoDate(start),
      endDate: toLocalIsoDate(end),
    };
  }

  const thisWeekMonday = addDays(
    localToday,
    -((localToday.getDay() + 6) % 7),
  );

  return {
    startDate: toLocalIsoDate(thisWeekMonday),
    endDate: toLocalIsoDate(addDays(thisWeekMonday, 6)),
  };
};


const formatPeriodDate = (value) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(`${value}T00:00:00`);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString(
    'en-AU',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
  );
};



const csvCell = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;


const downloadCsv = ({
  filename,
  headers,
  rows,
}) => {
  const body = [
    headers,
    ...rows,
  ].map((row) => row.map(csvCell).join(',')).join('\r\n');

  const blob = new Blob(
    [`\ufeff${body}`],
    { type: 'text/csv;charset=utf-8;' },
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};


const html = (value) => String(value ?? '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');


const printInvoiceSummary = ({
  contractorName,
  period,
  invoiceRows,
  summary,
}) => {
  const popup = window.open('', '_blank');

  if (!popup) {
    return false;
  }

  const rows = invoiceRows.map((row) => `
    <tr>
      <td>${html(row.date || '—')}</td>
      <td>${html(row.worker_name || '—')}</td>
      <td>${html(row.site_name || '—')}</td>
      <td>${html(row.total_hours ?? 0)}</td>
      <td>${html(row.rate_type || '—')}</td>
      <td>${html(
        row.hourly_rate == null ? '—' : formatCurrency(row.hourly_rate),
      )}</td>
      <td>${html(
        row.amount_ex_gst == null ? '—' : formatCurrency(row.amount_ex_gst),
      )}</td>
      <td>${html(
        String(row.invoice_status || 'attention').replaceAll('_', ' '),
      )}</td>
    </tr>
  `).join('');

  popup.document.write(`
    <!doctype html>
    <html lang="en-AU">
      <head>
        <meta charset="utf-8" />
        <title>Invoice Summary — ${html(contractorName)}</title>
        <style>
          body { color: #172033; font: 13px Arial, sans-serif; margin: 36px; }
          h1 { font-size: 24px; margin: 0 0 6px; }
          h2 { font-size: 15px; margin: 28px 0 10px; }
          .muted { color: #5d6b80; }
          .summary { margin: 20px 0; max-width: 360px; }
          .summary div { display:flex; justify-content:space-between; padding:8px 0; border-bottom:1px solid #dbe3ed; }
          table { border-collapse:collapse; width:100%; }
          th { background:#102b43; color:#fff; text-align:left; }
          th, td { border:1px solid #dbe3ed; padding:8px; vertical-align:top; }
          @media print { body { margin:16mm; } }
        </style>
      </head>
      <body>
        <h1>Contractor Invoice Summary</h1>
        <p class="muted">${html(contractorName)}</p>
        <p class="muted">
          Operational period: ${html(formatPeriodDate(period.startDate))}
          to ${html(formatPeriodDate(period.endDate))}
        </p>
        <div class="summary">
          <div><span>Approved hours</span><strong>${html(summary.invoice_approved_hours ?? 0)}</strong></div>
          <div><span>Invoice-ready hours</span><strong>${html(summary.invoice_ready_hours ?? 0)}</strong></div>
          <div><span>Amount ex GST</span><strong>${html(formatCurrency(summary.invoice_ready_ex_gst))}</strong></div>
          <div><span>GST (10%)</span><strong>${html(formatCurrency(summary.invoice_gst))}</strong></div>
          <div><span>Total incl GST</span><strong>${html(formatCurrency(summary.invoice_total_inc_gst))}</strong></div>
        </div>
        <h2>Approved work</h2>
        <table>
          <thead><tr>
            <th>Date</th><th>Team member</th><th>Site</th><th>Hours</th>
            <th>Rate type</th><th>Rate</th><th>Amount ex GST</th><th>Status</th>
          </tr></thead>
          <tbody>
            ${rows || '<tr><td colspan="8">No approved contractor work for this period.</td></tr>'}
          </tbody>
        </table>
        <p class="muted" style="margin-top:24px;font-size:11px">
          This is an operational invoice summary only and is not a tax invoice.
        </p>
      </body>
    </html>
  `);

  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 250);

  return true;
};


export default function ContractorOperationsPortal({
  view = 'dashboard',
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('all');
  const [selectedPreset, setSelectedPreset] = useState('this_month');
  const [period, setPeriod] = useState(
    () => getPeriodPreset('this_month'),
  );
  const [customStartDate, setCustomStartDate] = useState(
    () => getPeriodPreset('this_month').startDate,
  );
  const [customEndDate, setCustomEndDate] = useState(
    () => getPeriodPreset('this_month').endDate,
  );


  const load = async ({
    silent = false,
    selectedPeriod = period,
  } = {}) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError('');

      const response =
        await getContractorOperationsOverview({
          startDate: selectedPeriod.startDate,
          endDate: selectedPeriod.endDate,
        });

      setData(response);
    } catch (err) {
      setError(
        err?.response?.data?.detail
        || err?.message
        || 'Unable to load contractor operations.'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };


  useEffect(() => {
    load();
  }, []);


  const exportShifts = () => {
    downloadCsv({
      filename: `allocated-shifts-${period.startDate}-to-${period.endDate}.csv`,
      headers: ['Date', 'Team Member', 'Client', 'Site', 'Start', 'End', 'Hours', 'Status'],
      rows: shifts.map((shift) => [
        shift.date,
        shift.officer_name,
        shift.client_name,
        shift.site_name,
        shift.display_start_time || shift.start_time,
        shift.display_end_time || shift.end_time,
        shift.hours,
        String(
          shift.acceptance_status
          || shift.response_status
          || shift.shift_status
          || 'Allocated',
        ).replaceAll('_', ' '),
      ]),
    });
  };


  const exportTimesheets = () => {
    downloadCsv({
      filename: `contractor-timesheets-${period.startDate}-to-${period.endDate}.csv`,
      headers: ['Date', 'Team Member', 'Site', 'Clock In', 'Clock Out', 'Hours', 'Status'],
      rows: timesheets.map((row) => [
        row.date,
        row.worker_name,
        row.site_name,
        row.clock_in,
        row.clock_out,
        row.total_hours,
        row.status,
      ]),
    });
  };


  const exportInvoiceSummary = () => {
    downloadCsv({
      filename: `invoice-summary-${period.startDate}-to-${period.endDate}.csv`,
      headers: [
        'Date', 'Team Member', 'Site', 'Hours', 'Rate Type',
        'Hourly Rate ex GST', 'Amount ex GST', 'Status', 'Message',
      ],
      rows: invoiceRows.map((row) => [
        row.date,
        row.worker_name,
        row.site_name,
        row.total_hours,
        row.rate_type,
        row.hourly_rate,
        row.amount_ex_gst,
        String(row.invoice_status || 'attention').replaceAll('_', ' '),
        row.invoice_message,
      ]),
    });
  };


  const printInvoice = () => {
    if (!printInvoiceSummary({
      contractorName,
      period,
      invoiceRows,
      summary,
    })) {
      setError(
        'Allow pop-ups for this site, then try Print / Save PDF again.',
      );
    }
  };


  const selectPeriodPreset = (preset) => {
    const nextPeriod = getPeriodPreset(preset);

    setSelectedPreset(preset);
    setPeriod(nextPeriod);
    setCustomStartDate(nextPeriod.startDate);
    setCustomEndDate(nextPeriod.endDate);
    load({
      silent: true,
      selectedPeriod: nextPeriod,
    });
  };


  const applyCustomPeriod = () => {
    if (!customStartDate || !customEndDate) {
      setError('Select both a start and end date.');
      return;
    }

    if (customStartDate > customEndDate) {
      setError('The start date cannot be after the end date.');
      return;
    }

    const start = new Date(`${customStartDate}T00:00:00`);
    const end = new Date(`${customEndDate}T00:00:00`);

    if ((end - start) / 86400000 > 365) {
      setError('The selected period cannot exceed 366 days.');
      return;
    }

    const nextPeriod = {
      startDate: customStartDate,
      endDate: customEndDate,
    };

    setSelectedPreset('custom');
    setPeriod(nextPeriod);
    load({
      silent: true,
      selectedPeriod: nextPeriod,
    });
  };




  const contractorName = useMemo(
    () =>
      data?.contractor?.trading_name
      || data?.contractor?.company_name
      || 'Contractor',
    [data]
  );


  const summary = (
    data?.summary
    && typeof data.summary === 'object'
    && !Array.isArray(data.summary)
  ) ? data.summary : {};

  const team = normaliseList(data?.team);
  const shifts = normaliseList(data?.shifts);

  const lifecycleShifts = useMemo(
    () => shifts.map((shift) => ({
      ...shift,
      lifecycle: getShiftLifecycle(shift),
    })),
    [shifts],
  );

  const lifecycleCounts = useMemo(
    () => lifecycleShifts.reduce(
      (counts, shift) => {
        counts[shift.lifecycle.group] += 1;
        return counts;
      },
      {
        pending: 0,
        accepted: 0,
        in_progress: 0,
        completed: 0,
        exception: 0,
      },
    ),
    [lifecycleShifts],
  );

  const visibleShifts = useMemo(
    () => {
      if (lifecycleFilter === 'all') {
        return lifecycleShifts;
      }

      return lifecycleShifts.filter(
        (shift) => shift.lifecycle.group === lifecycleFilter,
      );
    },
    [lifecycleFilter, lifecycleShifts],
  );
  const timesheets = normaliseList(data?.timesheets);
  const invoiceRows = normaliseList(data?.invoice_rows);
  const showPeriodFilter = [
    'shifts',
    'timesheets',
    'invoices',
  ].includes(view);


  if (loading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center">
        <RefreshCw className="h-7 w-7 animate-spin text-blue-400" />
      </div>
    );
  }


  return (
    <div className="space-y-6 p-6">

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-zinc-400">
            Contractor Portal
          </p>

          <h1 className="mt-2 text-3xl font-semibold text-white">
            {
              view === 'team'
                ? 'Team'
                : view === 'shifts'
                  ? 'Allocated Shifts'
                  : view === 'timesheets'
                    ? 'Timesheets'
                    : view === 'compliance'
                      ? 'Compliance'
                      : view === 'invoices'
                        ? 'Invoice Summary'
                        : 'Dashboard'
            }
          </h1>

          <p className="mt-1 text-sm text-zinc-400">
            {contractorName}
          </p>
        </div>


        <button
          type="button"
          onClick={() => load({ silent: true })}
          disabled={refreshing}
          className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900 px-4 text-sm font-medium text-zinc-200 transition hover:bg-zinc-800 disabled:opacity-60"
        >
          <RefreshCw
            className={`h-4 w-4 ${
              refreshing ? 'animate-spin' : ''
            }`}
          />

          Refresh
        </button>

      </div>


      {showPeriodFilter && (
        <div className="rounded-xl border border-zinc-800 bg-[#151517] p-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="text-sm font-medium text-zinc-100">
                Operational period
              </p>
              <p className="mt-1 text-xs text-zinc-500">
                Showing {formatPeriodDate(period.startDate)} to {formatPeriodDate(period.endDate)}
              </p>
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
              <div className="flex flex-wrap gap-2">
                {[
                  ['this_week', 'This Week'],
                  ['last_week', 'Last Week'],
                  ['this_month', 'This Month'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => selectPeriodPreset(value)}
                    disabled={refreshing}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition disabled:opacity-60 ${
                      selectedPreset === value
                        ? 'border-blue-500 bg-blue-500/15 text-blue-300'
                        : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-end gap-2">
                <label className="text-xs text-zinc-500">
                  Start
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(event) => setCustomStartDate(event.target.value)}
                    className="mt-1 block min-h-[38px] rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-sm text-zinc-100 outline-none focus:border-blue-500"
                  />
                </label>

                <label className="text-xs text-zinc-500">
                  End
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(event) => setCustomEndDate(event.target.value)}
                    className="mt-1 block min-h-[38px] rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-sm text-zinc-100 outline-none focus:border-blue-500"
                  />
                </label>

                <button
                  type="button"
                  onClick={applyCustomPeriod}
                  disabled={refreshing}
                  className={`min-h-[38px] rounded-lg border px-3 text-sm font-medium transition disabled:opacity-60 ${
                    selectedPreset === 'custom'
                      ? 'border-blue-500 bg-blue-500/15 text-blue-300'
                      : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800'
                  }`}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </div>
      )}


      {!error && view === 'dashboard' && (
        <>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">

            <StatCard
              icon={Users}
              label="Team Members"
              value={summary.team_members}
            />

            <StatCard
              icon={CalendarDays}
              label="Upcoming Shifts"
              value={summary.upcoming_shifts}
            />

            <StatCard
              icon={Clock3}
              label="Submitted Timesheets"
              value={summary.submitted_timesheets}
            />

            <StatCard
              icon={CheckCircle2}
              label="Approved Timesheets"
              value={summary.approved_timesheets}
            />

          </div>


          <div className="grid gap-5 xl:grid-cols-2">

            <div className="rounded-xl border border-zinc-800 bg-[#151517]">

              <div className="border-b border-zinc-800 p-5">
                <h2 className="text-lg font-semibold text-white">
                  Team Overview
                </h2>

                <p className="mt-1 text-sm text-zinc-400">
                  Operational personnel linked to your organisation.
                </p>
              </div>


              <div className="p-5">

                {team.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-zinc-700 p-8 text-center">
                    <Users className="mx-auto h-7 w-7 text-zinc-500" />

                    <p className="mt-3 font-medium text-zinc-200">
                      No operational team members
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {team.slice(0, 6).map((worker) => (
                      <div
                        key={worker.id}
                        className="flex items-center justify-between gap-4 rounded-lg border border-zinc-800 bg-zinc-900/40 p-4"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="rounded-full border border-zinc-700 bg-zinc-900 p-2">
                            <UserRound className="h-4 w-4 text-zinc-400" />
                          </div>

                          <div className="min-w-0">
                            <p className="truncate font-medium text-zinc-100">
                              {worker.name}
                            </p>

                            <p className="truncate text-sm text-zinc-500">
                              {worker.email || 'No email'}
                            </p>
                          </div>
                        </div>

                        <span
                          className={
                            worker.is_active
                              ? 'rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-400'
                              : 'rounded-full bg-zinc-800 px-2.5 py-1 text-xs font-semibold text-zinc-400'
                          }
                        >
                          {
                            worker.is_active
                              ? 'Active'
                              : 'Inactive'
                          }
                        </span>
                      </div>
                    ))}
                  </div>
                )}

              </div>

            </div>


            <div className="rounded-xl border border-zinc-800 bg-[#151517]">

              <div className="border-b border-zinc-800 p-5">
                <h2 className="text-lg font-semibold text-white">
                  Operations
                </h2>

                <p className="mt-1 text-sm text-zinc-400">
                  Current contractor operational activity.
                </p>
              </div>


              <div className="grid gap-3 p-5 sm:grid-cols-2">

                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
                  <p className="text-sm text-zinc-500">
                    Historical shifts
                  </p>

                  <p className="mt-2 text-2xl font-semibold text-zinc-100">
                    {summary.historical_shifts || 0}
                  </p>
                </div>


                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
                  <p className="text-sm text-zinc-500">
                    Completed attendance hours
                  </p>

                  <p className="mt-2 text-2xl font-semibold text-zinc-100">
                    {summary.completed_attendance_hours || 0}
                  </p>
                </div>

              </div>

            </div>

          </div>

        </>
      )}


      {!error && view === 'team' && (

        <div className="rounded-xl border border-zinc-800 bg-[#151517]">

          <div className="border-b border-zinc-800 p-5">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-blue-400" />

              <div>
                <h2 className="text-lg font-semibold text-white">
                  Operational Team
                </h2>

                <p className="mt-1 text-sm text-zinc-400">
                  Personnel currently linked to {contractorName}.
                </p>
              </div>
            </div>
          </div>


          <div className="p-5">

            {team.length === 0 ? (

              <div className="rounded-lg border border-dashed border-zinc-700 p-10 text-center">
                <Users className="mx-auto h-8 w-8 text-zinc-500" />

                <p className="mt-3 font-medium text-zinc-200">
                  No operational team members found
                </p>

                <p className="mt-1 text-sm text-zinc-500">
                  Workers linked to this contractor will appear here.
                </p>
              </div>

            ) : (

              <div className="overflow-hidden rounded-lg border border-zinc-800">

                <div className="hidden grid-cols-[minmax(180px,1.4fr)_minmax(220px,1.5fr)_minmax(140px,1fr)_120px] gap-4 border-b border-zinc-800 bg-zinc-900/70 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500 md:grid">
                  <div>Name</div>
                  <div>Email</div>
                  <div>Role</div>
                  <div>Status</div>
                </div>


                {team.map((worker) => (

                  <div
                    key={worker.id}
                    className="grid gap-3 border-b border-zinc-800 px-4 py-4 last:border-b-0 md:grid-cols-[minmax(180px,1.4fr)_minmax(220px,1.5fr)_minmax(140px,1fr)_120px] md:items-center md:gap-4"
                  >

                    <div className="flex min-w-0 items-center gap-3">

                      <div className="rounded-full border border-zinc-700 bg-zinc-900 p-2">
                        <UserRound className="h-4 w-4 text-zinc-400" />
                      </div>

                      <div className="min-w-0">
                        <p className="truncate font-medium text-zinc-100">
                          {worker.name}
                        </p>

                        <p className="text-xs text-zinc-500 md:hidden">
                          Team member
                        </p>
                      </div>

                    </div>


                    <div className="flex min-w-0 items-center gap-2 text-sm text-zinc-400">
                      <Mail className="h-4 w-4 shrink-0 md:hidden" />
                      <span className="truncate">
                        {worker.email || '—'}
                      </span>
                    </div>


                    <div className="text-sm capitalize text-zinc-400">
                      {
                        worker.role
                          ? worker.role.replaceAll('_', ' ')
                          : worker.employee_type || '—'
                      }
                    </div>


                    <div>
                      <span
                        className={
                          worker.is_active
                            ? 'inline-flex rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-400'
                            : 'inline-flex rounded-full bg-zinc-800 px-2.5 py-1 text-xs font-semibold text-zinc-400'
                        }
                      >
                        {
                          worker.is_active
                            ? 'Active'
                            : 'Inactive'
                        }
                      </span>
                    </div>

                  </div>
                ))}

              </div>
            )}

          </div>

        </div>
      )}


      {!error && view === 'shifts' && (

        <div className="rounded-xl border border-zinc-800 bg-[#151517]">

          <div className="flex flex-col gap-4 border-b border-zinc-800 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">
                Allocated Shifts
              </h2>

              <p className="mt-1 text-sm text-zinc-400">
                Shifts allocated to personnel from {contractorName}.
              </p>
            </div>

            <button
              type="button"
              onClick={exportShifts}
              disabled={shifts.length === 0}
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-sm font-medium text-zinc-200 transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>

          <div className="grid gap-3 border-b border-zinc-800 p-5 sm:grid-cols-2 xl:grid-cols-5">
            {[
              ['pending', 'Awaiting response', 'amber'],
              ['accepted', 'Accepted', 'sky'],
              ['in_progress', 'In progress', 'violet'],
              ['completed', 'Completed', 'emerald'],
              ['exception', 'Needs follow-up', 'red'],
            ].map(([key, label, tone]) => (
              <button
                key={key}
                type="button"
                onClick={() => setLifecycleFilter(
                  lifecycleFilter === key ? 'all' : key,
                )}
                className={`rounded-lg border p-3 text-left transition ${
                  lifecycleFilter === key
                    ? `border-${tone}-400/70 bg-${tone}-500/10`
                    : 'border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900'
                }`}
              >
                <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  {label}
                </p>
                <p className="mt-1 text-2xl font-semibold text-zinc-100">
                  {lifecycleCounts[key]}
                </p>
              </button>
            ))}
          </div>

          {(lifecycleCounts.pending > 0 || lifecycleCounts.exception > 0) && (
            <div className="mx-5 mt-5 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200">
              {lifecycleCounts.pending > 0 && (
                <span>
                  {lifecycleCounts.pending} shift{lifecycleCounts.pending === 1 ? '' : 's'} awaiting officer acceptance.
                </span>
              )}
              {lifecycleCounts.pending > 0 && lifecycleCounts.exception > 0 && ' '}
              {lifecycleCounts.exception > 0 && (
                <span>
                  {lifecycleCounts.exception} shift{lifecycleCounts.exception === 1 ? '' : 's'} require follow-up.
                </span>
              )}
            </div>
          )}

          <div className="p-5">

            {visibleShifts.length === 0 ? (

              <div className="rounded-lg border border-dashed border-zinc-700 p-10 text-center">
                <CalendarDays className="mx-auto h-8 w-8 text-zinc-500" />

                <p className="mt-3 font-medium text-zinc-200">
                  No allocated shifts found
                </p>

                <p className="mt-1 text-sm text-zinc-500">
                  Contractor-assigned shifts will appear here.
                </p>
              </div>

            ) : (

              <div className="overflow-x-auto rounded-lg border border-zinc-800">

                <table className="w-full min-w-[1050px] text-left">

                  <thead className="bg-zinc-900/70 text-xs uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Team Member</th>
                      <th className="px-4 py-3">Client / Site</th>
                      <th className="px-4 py-3">Start</th>
                      <th className="px-4 py-3">End</th>
                      <th className="px-4 py-3">Hours</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {visibleShifts.map((shift) => (
                      <tr
                        key={shift.assignment_id}
                        className="border-t border-zinc-800 text-sm"
                      >
                        <td className="whitespace-nowrap px-4 py-4 text-zinc-300">
                          {shift.date || '—'}
                        </td>

                        <td className="px-4 py-4 font-medium text-zinc-100">
                          {shift.officer_name || '—'}
                        </td>

                        <td className="px-4 py-4">
                          <div className="text-zinc-200">
                            {shift.client_name || '—'}
                          </div>

                          <div className="mt-1 text-xs text-zinc-500">
                            {shift.site_name || '—'}
                          </div>
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-zinc-300">
                          {
                            shift.display_start_time
                            || shift.start_time
                            || '—'
                          }
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-zinc-300">
                          {
                            shift.display_end_time
                            || shift.end_time
                            || '—'
                          }
                        </td>

                        <td className="px-4 py-4 text-zinc-300">
                          {Number(shift.hours ?? 0).toLocaleString(
                            'en-AU',
                            { maximumFractionDigits: 2 },
                          )}
                        </td>

                        <td className="px-4 py-4">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${shift.lifecycle.className}`}>
                            {shift.lifecycle.label}
                          </span>
                          <p className="mt-1 max-w-[190px] text-xs text-zinc-500">
                            {shift.lifecycle.detail}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>

                </table>

              </div>
            )}

          </div>

        </div>
      )}


      {!error && view === 'timesheets' && (

        <div className="rounded-xl border border-zinc-800 bg-[#151517]">

          <div className="flex flex-col gap-4 border-b border-zinc-800 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">
                Timesheets
              </h2>

              <p className="mt-1 text-sm text-zinc-400">
                Contractor workforce timesheets for the selected operational period.
              </p>
            </div>

            <button
              type="button"
              onClick={exportTimesheets}
              disabled={timesheets.length === 0}
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-sm font-medium text-zinc-200 transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>


          <div className="p-5">

            {timesheets.length === 0 ? (

              <div className="rounded-lg border border-dashed border-zinc-700 p-10 text-center">
                <Clock3 className="mx-auto h-8 w-8 text-zinc-500" />

                <p className="mt-3 font-medium text-zinc-200">
                  No timesheets found
                </p>

                <p className="mt-1 text-sm text-zinc-500">
                  Workforce timesheets will appear here after attendance is recorded.
                </p>
              </div>

            ) : (

              <div className="overflow-x-auto rounded-lg border border-zinc-800">

                <table className="w-full min-w-[1000px] text-left">

                  <thead className="bg-zinc-900/70 text-xs uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Team Member</th>
                      <th className="px-4 py-3">Site</th>
                      <th className="px-4 py-3">Clock In</th>
                      <th className="px-4 py-3">Clock Out</th>
                      <th className="px-4 py-3">Hours</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {timesheets.map((row) => (
                      <tr
                        key={row.id}
                        className="border-t border-zinc-800 text-sm"
                      >
                        <td className="whitespace-nowrap px-4 py-4 text-zinc-300">
                          {row.date || '—'}
                        </td>

                        <td className="px-4 py-4 font-medium text-zinc-100">
                          {row.worker_name || '—'}
                        </td>

                        <td className="px-4 py-4 text-zinc-300">
                          {row.site_name || '—'}
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-zinc-300">
                          {row.clock_in || '—'}
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-zinc-300">
                          {row.clock_out || '—'}
                        </td>

                        <td className="px-4 py-4 text-zinc-300">
                          {row.total_hours ?? 0}
                        </td>

                        <td className="px-4 py-4">
                          <span
                            className={
                              row.status === 'approved'
                                ? 'inline-flex rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold capitalize text-emerald-400'
                                : row.status === 'submitted'
                                  ? 'inline-flex rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-semibold capitalize text-amber-300'
                                  : 'inline-flex rounded-full bg-zinc-800 px-2.5 py-1 text-xs font-semibold capitalize text-zinc-300'
                            }
                          >
                            {row.status || 'draft'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>

                </table>

              </div>
            )}

          </div>

        </div>
      )}



      {!error && view === 'compliance' && (

        <div className="space-y-5">

          <div className="grid gap-4 sm:grid-cols-3">

            <StatCard
              icon={CheckCircle2}
              label="Current"
              value={summary.compliance_current}
            />

            <StatCard
              icon={Clock3}
              label="Expiring Soon"
              value={summary.compliance_expiring}
            />

            <StatCard
              icon={ShieldCheck}
              label="Attention Required"
              value={summary.compliance_attention}
            />

          </div>


          <div className="rounded-xl border border-zinc-800 bg-[#151517]">

            <div className="border-b border-zinc-800 p-5">
              <h2 className="text-lg font-semibold text-white">
                Team Compliance
              </h2>

              <p className="mt-1 text-sm text-zinc-400">
                Current licence information for personnel linked to {contractorName}.
              </p>
            </div>


            <div className="p-5">

              {team.length === 0 ? (

                <div className="rounded-lg border border-dashed border-zinc-700 p-10 text-center">
                  <ShieldCheck className="mx-auto h-8 w-8 text-zinc-500" />

                  <p className="mt-3 font-medium text-zinc-200">
                    No team compliance records found
                  </p>
                </div>

              ) : (

                <div className="space-y-4">

                  {team.map((worker) => {

                    const licences = [
                      worker.compliance?.security_licence,
                      worker.compliance?.driver_licence,
                    ].filter(Boolean);

                    return (
                      <div
                        key={worker.id}
                        className="overflow-hidden rounded-xl border border-zinc-800"
                      >

                        <div className="flex flex-col gap-2 border-b border-zinc-800 bg-zinc-900/40 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">

                          <div>
                            <p className="font-semibold text-zinc-100">
                              {worker.name}
                            </p>

                            <p className="mt-1 text-sm text-zinc-500">
                              {worker.email || 'No email recorded'}
                            </p>
                          </div>

                          <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                            {worker.role?.replaceAll('_', ' ') || 'Team member'}
                          </span>

                        </div>


                        <div className="divide-y divide-zinc-800">

                          {licences.map((item) => (
                            <div
                              key={item.type}
                              className="grid gap-4 px-5 py-4 md:grid-cols-[1.2fr_1fr_1fr_160px] md:items-center"
                            >

                              <div>
                                <p className="text-sm font-medium text-zinc-200">
                                  {item.type}
                                </p>

                                <p className="mt-1 text-xs text-zinc-500">
                                  Licence
                                </p>
                              </div>


                              <div>
                                <p className="text-xs uppercase tracking-wide text-zinc-500">
                                  Number
                                </p>

                                <p className="mt-1 text-sm text-zinc-200">
                                  {item.number || 'Not recorded'}
                                </p>
                              </div>


                              <div>
                                <p className="text-xs uppercase tracking-wide text-zinc-500">
                                  Expiry
                                </p>

                                <p className="mt-1 text-sm text-zinc-200">
                                  {formatComplianceDate(item.expiry_date)}
                                </p>
                              </div>


                              <div className="md:text-right">
                                <ComplianceBadge
                                  item={item}
                                />
                              </div>

                            </div>
                          ))}

                        </div>

                      </div>
                    );
                  })}

                </div>
              )}

            </div>

          </div>

        </div>
      )}



      {!error && view === 'invoices' && (

        <div className="space-y-5">

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">

            <StatCard
              icon={Clock3}
              label="Approved Hours"
              value={summary.invoice_approved_hours ?? 0}
            />

            <StatCard
              icon={CheckCircle2}
              label="Invoice Ready Hours"
              value={summary.invoice_ready_hours ?? 0}
            />

            <StatCard
              icon={FileText}
              label="Amount ex GST"
              value={formatCurrency(
                summary.invoice_ready_ex_gst
              )}
            />

            <StatCard
              icon={FileText}
              label="Total incl GST"
              value={formatCurrency(
                summary.invoice_total_inc_gst
              )}
            />

            <StatCard
              icon={ShieldCheck}
              label="Attention Required"
              value={summary.invoice_attention ?? 0}
            />

          </div>


          <div className="rounded-xl border border-zinc-800 bg-[#151517]">

            <div className="flex flex-col gap-4 border-b border-zinc-800 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Approved Work
                </h2>

                <p className="mt-1 text-sm text-zinc-400">
                  Approved contractor hours and explicitly configured contractor rates for {contractorName}.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={exportInvoiceSummary}
                  disabled={invoiceRows.length === 0}
                  className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-sm font-medium text-zinc-200 transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </button>

                <button
                  type="button"
                  onClick={printInvoice}
                  className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-blue-500/50 bg-blue-500/10 px-3 text-sm font-medium text-blue-300 transition hover:bg-blue-500/20"
                >
                  <Printer className="h-4 w-4" />
                  Print / Save PDF
                </button>
              </div>
            </div>


            <div className="p-5">

              {invoiceRows.length === 0 ? (

                <div className="rounded-lg border border-dashed border-zinc-700 p-10 text-center">

                  <FileText className="mx-auto h-8 w-8 text-zinc-500" />

                  <p className="mt-3 font-medium text-zinc-200">
                    No approved contractor work found
                  </p>

                  <p className="mt-1 text-sm text-zinc-500">
                    Approved timesheets will appear here when they are ready for invoice reconciliation.
                  </p>

                </div>

              ) : (

                <div className="overflow-x-auto rounded-lg border border-zinc-800">

                  <table className="w-full min-w-[1150px] text-left">

                    <thead className="bg-zinc-900/70 text-xs uppercase tracking-wide text-zinc-500">
                      <tr>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Team Member</th>
                        <th className="px-4 py-3">Site</th>
                        <th className="px-4 py-3">Hours</th>
                        <th className="px-4 py-3">Rate Type</th>
                        <th className="px-4 py-3">Rate</th>
                        <th className="px-4 py-3">Amount ex GST</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>

                    <tbody>

                      {invoiceRows.map((row) => (

                        <tr
                          key={row.id}
                          className="border-t border-zinc-800 text-sm"
                        >

                          <td className="whitespace-nowrap px-4 py-4 text-zinc-300">
                            {row.date || '—'}
                          </td>

                          <td className="px-4 py-4 font-medium text-zinc-100">
                            {row.worker_name || '—'}
                          </td>

                          <td className="px-4 py-4 text-zinc-300">
                            {row.site_name || '—'}
                          </td>

                          <td className="px-4 py-4 text-zinc-300">
                            {row.total_hours ?? 0}
                          </td>

                          <td className="px-4 py-4 capitalize text-zinc-300">
                            {
                              row.rate_type
                                ? row.rate_type.replaceAll('_', ' ')
                                : '—'
                            }
                          </td>

                          <td className="px-4 py-4 text-zinc-300">
                            {
                              row.hourly_rate == null
                                ? 'Not configured'
                                : formatCurrency(row.hourly_rate)
                            }
                          </td>

                          <td className="px-4 py-4 font-medium text-zinc-100">
                            {
                              row.amount_ex_gst == null
                                ? '—'
                                : formatCurrency(row.amount_ex_gst)
                            }
                          </td>

                          <td className="px-4 py-4">

                            {row.invoice_status === 'ready' ? (

                              <span className="inline-flex rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-400">
                                Invoice Ready
                              </span>

                            ) : (

                              <div>
                                <span className="inline-flex rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-300">
                                  Attention Required
                                </span>

                                <p className="mt-1 max-w-xs text-xs text-zinc-500">
                                  {row.invoice_message}
                                </p>
                              </div>

                            )}

                          </td>

                        </tr>
                      ))}

                    </tbody>

                  </table>

                </div>
              )}


              <div className="mt-5 flex justify-end">

                <div className="w-full max-w-sm space-y-2 rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 text-sm">

                  <div className="flex justify-between text-zinc-400">
                    <span>Amount ex GST</span>
                    <span>
                      {formatCurrency(
                        summary.invoice_ready_ex_gst
                      )}
                    </span>
                  </div>

                  <div className="flex justify-between text-zinc-400">
                    <span>GST (10%)</span>
                    <span>
                      {formatCurrency(
                        summary.invoice_gst
                      )}
                    </span>
                  </div>

                  <div className="flex justify-between border-t border-zinc-700 pt-2 font-semibold text-white">
                    <span>Total incl GST</span>
                    <span>
                      {formatCurrency(
                        summary.invoice_total_inc_gst
                      )}
                    </span>
                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>
      )}


    </div>
  );
}
